import { and, asc, count, desc, eq, gt, inArray, ne, sql } from 'drizzle-orm'

import type { Database } from '../client'
import type { SelectHomeConnectRequest } from '../schema'
import type { DatabaseTransaction } from './child-merge'
import {
  HOME_CONNECT_REQUEST_TTL_DAYS,
  HomeConnectRequest,
  member,
  Notification,
  NotificationEvent,
  organization,
  team,
  teamMember,
  user,
} from '../schema'
import { findSchoolAdminRecipients } from './school-removal'

/**
 * A handle that may be either a pooled connection or an open transaction.
 *
 * The two helpers that take this are called from inside `deleteChildren`'s transaction, where a
 * `Database`-typed parameter would still compile but run its statements on a different connection —
 * outside the transaction, and therefore not rolled back with it.
 */
type Queryable = Database | DatabaseTransaction

/**
 * Reads and writes for Sambung Sekolah-Rumah.
 *
 * The load-bearing distinction: a child IS connected when it holds a `member` row in an organization
 * whose `type` array contains `'family'`. The request table only records that someone asked. Reading
 * connectedness from `status = 'ACCEPTED'` would let a stale row claim a link that no longer exists,
 * and would miss a child linked by any other path.
 */

/** SQL predicate for "this organization is a family". `organization.type` is a text array. */
export const IS_FAMILY_ORG = sql`'family' = ANY(${organization.type})`

/** SQL predicate for "this organization is a school". `organization.type` is a text array. */
export const IS_SCHOOL_ORG = sql`'school' = ANY(${organization.type})`

/** A request is only actionable while it is PENDING and inside its window. */
export function isLiveRequest(request: Pick<SelectHomeConnectRequest, 'status' | 'expiresAt'>, now = new Date()) {
  return request.status === 'PENDING' && request.expiresAt > now
}

/** `requestedAt` plus the fixed TTL. Kept here so the router and the resend path cannot drift. */
export function homeConnectExpiryFrom(start: Date): Date {
  return new Date(start.getTime() + HOME_CONNECT_REQUEST_TTL_DAYS * 24 * 60 * 60 * 1000)
}

export interface FamilyConnection {
  familyOrganizationId: string
  familyOrganizationName: string
  /** The family organization's owner — the parent the school would contact. */
  parentUserId: string | null
  parentName: string | null
  parentEmail: string | null
}

/**
 * The family organization a child already belongs to, with its owner, or `null` when the child is not
 * connected to anyone. The owner is the parent; a family organization is created by the parent who owns
 * it (`Keluarga {name}`), and the product caps each user at one.
 */
export async function findFamilyConnectionForChild(
  db: Queryable,
  childUserId: string,
): Promise<FamilyConnection | null> {
  const [row] = await db
    .select({
      familyOrganizationId: organization.id,
      familyOrganizationName: organization.name,
      parentUserId: sql<string | null>`(
        SELECT owner_user.id
        FROM ${member} AS owner_member
        JOIN ${user} AS owner_user ON owner_user.id = owner_member.user_id
        WHERE owner_member.organization_id = ${organization.id}
          AND owner_member.role = 'owner'
        ORDER BY owner_member.created_at ASC
        LIMIT 1
      )`,
      parentName: sql<string | null>`(
        SELECT owner_user.name
        FROM ${member} AS owner_member
        JOIN ${user} AS owner_user ON owner_user.id = owner_member.user_id
        WHERE owner_member.organization_id = ${organization.id}
          AND owner_member.role = 'owner'
        ORDER BY owner_member.created_at ASC
        LIMIT 1
      )`,
      parentEmail: sql<string | null>`(
        SELECT owner_user.email
        FROM ${member} AS owner_member
        JOIN ${user} AS owner_user ON owner_user.id = owner_member.user_id
        WHERE owner_member.organization_id = ${organization.id}
          AND owner_member.role = 'owner'
        ORDER BY owner_member.created_at ASC
        LIMIT 1
      )`,
    })
    .from(member)
    .innerJoin(organization, eq(organization.id, member.organizationId))
    .where(and(eq(member.userId, childUserId), IS_FAMILY_ORG))
    .orderBy(asc(member.createdAt))
    .limit(1)

  return row ?? null
}

export interface ChildFamilyConnection extends FamilyConnection {
  childUserId: string
  childName: string
  childImage: string | null
  /**
   * The child also holds a membership in a school organization — Sambung Sekolah-Rumah is in
   * place. A child their parent created at home has a family row and nothing else.
   */
  inSchool: boolean
}

/**
 * {@link findFamilyConnectionForChild} for many children in one round trip, keyed by child id.
 *
 * Same semantics, deliberately: a family organization, earliest `member.createdAt` wins when a
 * child somehow sits in two, and the parent is the earliest-created `owner` member row. Children
 * with no family connection are simply absent from the map.
 *
 * Batched because its caller is the guardian mirror, which runs once per notification producer —
 * a cron pass over a forty-child course would otherwise be forty round trips.
 *
 * **Child accounts only**, unlike the single-child version, which is called with an id already
 * known to be a child's. This one is handed a producer's recipient list, which can hold teachers
 * and other parents — and an adult is the owner of their *own* family organization, so without
 * this filter a co-parent recipient would resolve to the other parent and be mirrored as if they
 * were a child. `userType`, never the synthetic email.
 *
 * The child's own name and avatar ride along: every caller needs them for the snapshot, and they
 * are already on the join. So does `inSchool`, which the guardian mirror needs to tell a
 * home-school-connected child from one who lives only in the family.
 */
export async function findFamilyConnectionsForChildren(
  db: Queryable,
  childUserIds: string[],
): Promise<Map<string, ChildFamilyConnection>> {
  const ids = [...new Set(childUserIds)]
  if (ids.length === 0) return new Map()

  const rows = await db
    .select({
      childUserId: member.userId,
      childName: user.name,
      childImage: user.image,
      familyOrganizationId: organization.id,
      familyOrganizationName: organization.name,
      parentUserId: sql<string | null>`(
        SELECT owner_user.id
        FROM ${member} AS owner_member
        JOIN ${user} AS owner_user ON owner_user.id = owner_member.user_id
        WHERE owner_member.organization_id = ${organization.id}
          AND owner_member.role = 'owner'
        ORDER BY owner_member.created_at ASC
        LIMIT 1
      )`,
      parentName: sql<string | null>`(
        SELECT owner_user.name
        FROM ${member} AS owner_member
        JOIN ${user} AS owner_user ON owner_user.id = owner_member.user_id
        WHERE owner_member.organization_id = ${organization.id}
          AND owner_member.role = 'owner'
        ORDER BY owner_member.created_at ASC
        LIMIT 1
      )`,
      parentEmail: sql<string | null>`(
        SELECT owner_user.email
        FROM ${member} AS owner_member
        JOIN ${user} AS owner_user ON owner_user.id = owner_member.user_id
        WHERE owner_member.organization_id = ${organization.id}
          AND owner_member.role = 'owner'
        ORDER BY owner_member.created_at ASC
        LIMIT 1
      )`,
      inSchool: sql<boolean>`EXISTS (
        SELECT 1
        FROM ${member} AS school_member
        JOIN ${organization} AS school_org ON school_org.id = school_member.organization_id
        WHERE school_member.user_id = ${member.userId}
          AND 'school' = ANY(school_org.type)
      )`,
    })
    .from(member)
    .innerJoin(organization, eq(organization.id, member.organizationId))
    .innerJoin(user, eq(user.id, member.userId))
    .where(and(inArray(member.userId, ids), eq(user.userType, 'child'), IS_FAMILY_ORG))
    .orderBy(asc(member.userId), asc(member.createdAt))

  // "Earliest membership wins" applied here rather than with DISTINCT ON: the ordering above
  // already guarantees the first row per child is the oldest one, and keeping it in JS leaves the
  // single-child query and this one reading the same way.
  const byChild = new Map<string, ChildFamilyConnection>()
  for (const row of rows) {
    if (!byChild.has(row.childUserId)) byChild.set(row.childUserId, row)
  }

  return byChild
}

export interface SchoolConnection {
  schoolOrganizationId: string
  schoolOrganizationName: string
  schoolOrganizationImage: string | null
  schoolOrganizationPin: string | null
}

/**
 * The school organization a child already belongs to, or `null` when the child is not a school
 * member. Mirrors {@link findFamilyConnectionForChild}: earliest membership wins on the rare chance a
 * child sits in more than one school. Used to show "which school" alongside "which parent" once a
 * child is connected both ways — never to gate who counts as a school caller (that reads `member`
 * directly against the caller's own active organization).
 */
export async function findSchoolConnectionForChild(
  db: Database,
  childUserId: string,
): Promise<SchoolConnection | null> {
  const [row] = await db
    .select({
      schoolOrganizationId: organization.id,
      schoolOrganizationName: organization.name,
      schoolOrganizationImage: organization.logo,
      schoolOrganizationPin: organization.pin,
    })
    .from(member)
    .innerJoin(organization, eq(organization.id, member.organizationId))
    .where(and(eq(member.userId, childUserId), IS_SCHOOL_ORG))
    .orderBy(asc(member.createdAt))
    .limit(1)

  return row ?? null
}

/** The live request for a child, if any. There can be at most one — a partial unique index enforces it. */
export async function findLiveRequestForChild(
  db: Database,
  childUserId: string,
): Promise<SelectHomeConnectRequest | null> {
  const [row] = await db
    .select()
    .from(HomeConnectRequest)
    .where(
      and(
        eq(HomeConnectRequest.childUserId, childUserId),
        eq(HomeConnectRequest.status, 'PENDING'),
        gt(HomeConnectRequest.expiresAt, new Date()),
      ),
    )
    .limit(1)

  return row ?? null
}

/**
 * Ids of the live requests addressed to an email address, newest first.
 *
 * Matched case-insensitively, as the parent side does everywhere: `parentEmail` is stored lowercased
 * but nothing normalises the account's own copy.
 */
export async function findLiveRequestIdsForParentEmail(db: Database, email: string): Promise<string[]> {
  const rows = await db
    .select({ id: HomeConnectRequest.id })
    .from(HomeConnectRequest)
    .where(
      and(
        sql`lower(${HomeConnectRequest.parentEmail}) = lower(${email})`,
        eq(HomeConnectRequest.status, 'PENDING'),
        gt(HomeConnectRequest.expiresAt, new Date()),
      ),
    )
    .orderBy(desc(HomeConnectRequest.requestedAt))

  return rows.map((row) => row.id)
}

/**
 * Gives a parent the feed card a request could not give them when it was sent.
 *
 * A request to an address with no account writes its `connect.requested` event with only the
 * teacher's ACTOR receipt — there was nobody to address a RECIPIENT row to. Once that parent signs
 * up, this adds the missing row for every live request sent to their email, so `/pesan` shows the
 * request instead of an empty feed. Idempotent: the unique `(eventId, userId, audience)` index turns
 * a second call into a no-op.
 *
 * The caller must have verified the email — the address is the only thing tying the account to the
 * request. Returns the live request ids, newest first.
 */
export async function attachLiveRequestsToParent(
  db: Database,
  { parentUserId, email }: { parentUserId: string; email: string },
): Promise<string[]> {
  const requestIds = await findLiveRequestIdsForParentEmail(db, email)
  if (requestIds.length === 0) return []

  const events = await db
    .select({ id: NotificationEvent.id })
    .from(NotificationEvent)
    .where(
      and(
        eq(NotificationEvent.type, 'connect.requested'),
        eq(NotificationEvent.entityType, 'HOME_CONNECT_REQUEST'),
        inArray(NotificationEvent.entityId, requestIds),
      ),
    )

  if (events.length > 0) {
    const inserted = await db
      .insert(Notification)
      .values(
        events.map((event) => ({
          eventId: event.id,
          userId: parentUserId,
          audience: 'RECIPIENT' as const,
          category: 'CONNECT' as const,
        })),
      )
      .onConflictDoNothing()
      .returning()

    if (inserted.length > 0) {
      await db
        .update(NotificationEvent)
        .set({ recipientCount: sql`${NotificationEvent.recipientCount} + 1` })
        .where(
          inArray(
            NotificationEvent.id,
            inserted.map((row) => row.eventId),
          ),
        )
    }
  }

  return requestIds
}

/**
 * A request by id, scoped to the school that owns it. Every school-side mutation goes through this so a
 * request id from another organization is a not-found rather than an authorization message.
 */
export async function findRequestForSchool(
  db: Database,
  requestId: string,
  schoolOrganizationId: string,
): Promise<SelectHomeConnectRequest | null> {
  const [row] = await db
    .select()
    .from(HomeConnectRequest)
    .where(and(eq(HomeConnectRequest.id, requestId), eq(HomeConnectRequest.schoolOrganizationId, schoolOrganizationId)))
    .limit(1)

  return row ?? null
}

/**
 * How many organizations a user belongs to.
 *
 * `deleteChildren` needs this: once a child is in a school AND a family, "keluarkan dari sekolah" must
 * drop the membership, not the user row that every seat and progress row keys off.
 */
export async function countMembershipsByUser(db: Queryable, userIds: string[]): Promise<Map<string, number>> {
  if (userIds.length === 0) return new Map()

  const rows = await db
    .select({ userId: member.userId, total: count() })
    .from(member)
    .where(inArray(member.userId, userIds))
    .groupBy(member.userId)

  return new Map(rows.map((row) => [row.userId, row.total]))
}

/**
 * Flip requests whose window has closed. Reads already treat an out-of-window PENDING row as expired, so
 * this only keeps the stored state honest for reporting and for the one-pending-per-child index.
 */
export async function expireStaleRequests(db: Database, now = new Date()): Promise<number> {
  const rows = await db
    .update(HomeConnectRequest)
    .set({ status: 'EXPIRED' })
    .where(and(eq(HomeConnectRequest.status, 'PENDING'), sql`${HomeConnectRequest.expiresAt} <= ${now}`))
    .returning()

  return rows.length
}

/**
 * Settle one child's lapsed request before anything tries to insert a new one.
 *
 * The read guards treat a PENDING row past its window as absent, but
 * `idx_home_connect_request_one_pending_per_child` is predicated on `status = 'PENDING'` alone and
 * does not know about `expiresAt`. Between a request lapsing and the nightly sweep reaching it, those
 * two views disagree and the insert fails with a raw unique violation — a 500 rather than a message.
 * Calling this first removes the disagreement instead of teaching the index about time.
 */
export async function expireLapsedRequestForChild(
  db: Database,
  childUserId: string,
  now = new Date(),
): Promise<number> {
  const rows = await db
    .update(HomeConnectRequest)
    .set({ status: 'EXPIRED' })
    .where(
      and(
        eq(HomeConnectRequest.childUserId, childUserId),
        eq(HomeConnectRequest.status, 'PENDING'),
        sql`${HomeConnectRequest.expiresAt} <= ${now}`,
      ),
    )
    .returning()

  return rows.length
}

/**
 * Cancel every live request for these children. Used when a school removes a student — leaving a live
 * request behind would let a parent accept a link to a child the school no longer holds.
 */
export async function cancelLiveRequestsForChildren(db: Queryable, childUserIds: string[]): Promise<number> {
  if (childUserIds.length === 0) return 0

  const rows = await db
    .update(HomeConnectRequest)
    .set({ status: 'CANCELLED', cancelledAt: new Date() })
    .where(and(inArray(HomeConnectRequest.childUserId, childUserIds), eq(HomeConnectRequest.status, 'PENDING')))
    .returning()

  return rows.length
}

/**
 * The adult account behind an email address, if there is one.
 *
 * Children hold synthetic `child-{orgId}-{timestamp}@pibo.local` addresses, so an address that resolves
 * to a `child` user is a data error rather than a parent — the caller rejects it.
 */
export async function findAdultByEmail(db: Database, email: string) {
  const [row] = await db
    .select({ id: user.id, name: user.name, email: user.email, userType: user.userType })
    .from(user)
    .where(eq(user.email, email.toLowerCase()))
    .limit(1)

  return row ?? null
}

/** The family organization owned by a user, if they have created one. Capped at one by the product. */
export async function findOwnedFamilyOrganization(db: Database, userId: string) {
  const [row] = await db
    .select({ id: organization.id, name: organization.name })
    .from(member)
    .innerJoin(organization, eq(organization.id, member.organizationId))
    .where(and(eq(member.userId, userId), eq(member.role, 'owner'), IS_FAMILY_ORG))
    .orderBy(asc(member.createdAt))
    .limit(1)

  return row ?? null
}

/** True when the child is a member of the given organization. Guards every school-side mutation. */
export async function isChildOfOrganization(
  db: Database,
  childUserId: string,
  organizationId: string,
): Promise<boolean> {
  const [row] = await db
    .select({ id: member.id })
    .from(member)
    .innerJoin(user, eq(user.id, member.userId))
    .where(and(eq(member.userId, childUserId), eq(member.organizationId, organizationId), eq(user.userType, 'child')))
    .limit(1)

  return row !== undefined
}

/** Schools other than this one that the child belongs to. Used only for diagnostics in error copy. */
export async function countOtherOrganizations(
  db: Database,
  childUserId: string,
  organizationId: string,
): Promise<number> {
  const [row] = await db
    .select({ total: count() })
    .from(member)
    .where(and(eq(member.userId, childUserId), ne(member.organizationId, organizationId)))

  return row?.total ?? 0
}

/**
 * School-side audience for a Sambung Sekolah-Rumah identity-change notification (parent renamed
 * the child, or reissued their PIN): every owner/admin of the school, plus every teacher on the
 * student's class(es).
 *
 * A `teamMember` row does not say whether its user is a teacher or a fellow student — both join a
 * class the same way — so teachers are told apart by `userType = 'main'`. Owner/admin and teacher
 * sets can overlap; the caller (`emit`) already dedupes `recipientIds`, so this just concatenates.
 */
export async function findClassNotificationRecipients(
  db: Database,
  args: { schoolOrganizationId: string; studentUserId: string },
): Promise<string[]> {
  const { schoolOrganizationId, studentUserId } = args

  const ownerAndAdminRows = await findSchoolAdminRecipients(db, schoolOrganizationId)

  const studentTeamRows = await db
    .select({ teamId: teamMember.teamId })
    .from(teamMember)
    .innerJoin(team, and(eq(team.id, teamMember.teamId), eq(team.organizationId, schoolOrganizationId)))
    .where(eq(teamMember.userId, studentUserId))

  const teamIds = studentTeamRows.map((row) => row.teamId)

  const classTeacherRows =
    teamIds.length === 0
      ? []
      : await db
          .select({ userId: teamMember.userId })
          .from(teamMember)
          .innerJoin(user, eq(user.id, teamMember.userId))
          .where(and(inArray(teamMember.teamId, teamIds), eq(user.userType, 'main')))

  return [...new Set([...ownerAndAdminRows.map((row) => row.userId), ...classTeacherRows.map((row) => row.userId)])]
}
